/**
 * Operations → Test Report.
 *
 * Every generation, whether or not it produced a report. Listing only the
 * reports would make the operation look idle exactly when it is failing, which
 * is backwards: a refused generation is the row somebody came here for. Jobs
 * and reports are merged on the sitting, and a report supersedes its own job.
 *
 * The refusal reason is shown in full. This operation refuses on purpose — a
 * report that states a figure the ledger does not hold is rejected and nothing
 * is stored — so the message is a finding about the model or the ledger, not
 * noise to truncate.
 */

import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { DataTable, Badge, type ColumnDef } from '@shared-ui/components';
import { adminApi, type TestReportRunRow } from '../../api/adminApi';
import './TestReportList.css';

const STATUS_BADGE: Record<string, 'success' | 'error' | 'warning' | 'default'> = {
  succeeded: 'success',
  failed: 'error',
  running: 'warning',
  queued: 'warning',
};

export function TestReportList() {
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['test-report-runs'],
    queryFn: () => adminApi.getTestReportRuns(50),
  });

  const runs = data?.runs ?? [];

  const columns: ColumnDef<TestReportRunRow>[] = [
    {
      key: 'at',
      header: 'When',
      render: r => (r.at ? format(new Date(r.at), 'MMM d, HH:mm') : '—'),
    },
    {
      key: 'student',
      header: 'Student',
      render: r => (
        <div>
          <div>{r.studentName ?? <span className="trl-mono">{r.studentId.slice(0, 8)}</span>}</div>
          <div className="trl-sub trl-mono">{r.sessionId.slice(0, 8)}</div>
        </div>
      ),
    },
    {
      key: 'paper',
      header: 'Paper',
      render: r => (
        <div>
          <div>{r.paperTitle ?? '—'}</div>
          {/* The first thing to check when a report talks about conversion and
              never about marks. */}
          {r.hasMarkingScheme === false && <div className="trl-sub">no marking scheme</div>}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: r => (
        <Badge variant={STATUS_BADGE[r.status] ?? 'default'} size="sm">
          {r.status === 'succeeded' && r.reportStatus ? r.reportStatus : r.status}
        </Badge>
      ),
    },
    {
      key: 'detail',
      header: 'Outcome',
      render: r => (r.reportId
        ? <span className="trl-open">open the report</span>
        : r.error
          ? <span className="trl-error">{r.error}</span>
          : <span className="trl-sub">—</span>),
    },
  ];

  return (
    <div className="trl">
      {error && <div className="trl-banner">{error instanceof Error ? error.message : 'Could not load the runs.'}</div>}

      <DataTable<TestReportRunRow>
        columns={columns}
        data={runs}
        rowKey={r => r.sessionId}
        loading={isLoading}
        compact
        emptyMessage="No test report has been generated yet."
        // Only a run that produced something has somewhere to go; a refusal's
        // reason is already on the row.
        rowClickable={r => !!r.reportId}
        onRowClick={r => r.reportId && navigate(`/assignments-tests/report/${r.reportId}?studentId=${r.studentId}`)}
        rowHref={r => (r.reportId ? `/assignments-tests/report/${r.reportId}?studentId=${r.studentId}` : undefined)}
        rowTestId="test-report-run-row"
      />
    </div>
  );
}
